import { createSelector } from "../../lib/util/ReduxUtils";
import * as AuthUtils from "../../lib/util/AuthUtils";
import { isAuthLevelAccessAnyFleet } from "../../lib/util/AuthUtils";
import * as FormatUtils from "../../lib/util/FormatUtils";
import * as StringUtils from "../../lib/util/StringUtils";
import * as Flags from "../../lib/Flags";
import * as FleetSelectors from "../Fleet/Fleet.selector";
import * as ProgramSelectors from "../Program/Program.selector";
import { StoreState } from "../RootReducer";
import { User } from "../../lib/types/UserTypes";
import { ScopeInfo } from "../../lib/types/MiscTypes";
import { AuthLevel } from "../../lib/consts/AuthConstants";
import { FIVE_KEYS_PILLARS, PILLARS } from "../../lib/Pillars";
import * as HardwarePillars from "../../lib/HardwarePillars";
import { HW_APP_TYPE } from "../../lib/consts/HardwareConstants";
import { TRENDS_AVAILABLE_KEY } from "../../StorageKeys";
import * as StorageUtils from "../../lib/util/StorageUtils";
import { isFiveKeysTheme } from "lib/util/ThemeUtils";

export const selectLoggedInUser = (state: StoreState) => state.User.user;

export const selectIsLoggedIn = (state: StoreState) =>
	Boolean(selectLoggedInUser(state)?.uid);

export const selectLoggedInUserId = (state: StoreState) => {
	const user = selectLoggedInUser(state);
	return user && user.uid;
};

export const getUserFleetId = (user: User) => user && user.fleetId;

export const getUserOverrideFleetId = (user: User) =>
	user && user.fleetIdOverride;

export const selectUserScopeFleetId = createSelector(
	[getUserFleetId, getUserOverrideFleetId],
	(fleetId, overrideFleetId) => overrideFleetId || fleetId,
);

export const selectCurrentScopeFleetId = (state: StoreState) => {
	const user = selectLoggedInUser(state);
	return user && selectUserScopeFleetId(user);
};

export const selectCurrentScopeFleet = (state: StoreState) =>
	FleetSelectors.selectCurrentScopeFleet(state);

export const selectCurrentScopeGroupId = (state: StoreState) =>
	state.Groups.selectedGroup;

export const selectCurrentScopeGroup = createSelector(
	[selectCurrentScopeGroupId, FleetSelectors.getCurrentScopeFleetGroupsFlat],
	(groupId, groups) =>
		(groups.value && groups.value.find((group) => group.id === groupId)) ||
		null,
);

export const selectLoggedInUserGroupId = (state: StoreState) => {
	const loggedInUser = selectLoggedInUser(state);
	const emulatedUserLevel = AuthUtils.getEmulatedUserLevel();

	if (emulatedUserLevel) {
		const scopeGroupId = selectCurrentScopeGroupId(state);
		const emulatedGroupId = AuthUtils.getEmulatedGroupId();
		return emulatedGroupId || scopeGroupId;
	}

	return loggedInUser?.group;
};

export const selectLoggedInUserGroup = createSelector(
	[selectLoggedInUserGroupId, FleetSelectors.getCurrentScopeFleetGroupsFlat],
	(groupId, groups) =>
		(groups.value && groups.value.find((group) => group.id === groupId)) ||
		null,
);

export const selectIsCurrentScopeOstrichMode = (
	state: StoreState,
	ignoreSuperAdmin = false,
) => {
	const fleet = FleetSelectors.selectCurrentScopeFleet(state);

	const group = selectCurrentScopeGroup(state);
	const force = Flags.FORCE_OSTRICH_MODE.get();
	if (typeof force === "boolean") {
		return force;
	}
	if (AuthUtils.isSuperAdmin() && !ignoreSuperAdmin) {
		return false;
	}
	if (!selectCurrentScopeHasTrends(state)) {
		return true;
	}

	return group ? group.ostrichMode : fleet.value && fleet.value.ostrichMode;
};

export const selectCurrentScopeHasTrends = (state: StoreState) => {
	const allFleets = FleetSelectors.getAllFleets(state);
	const fleetId = selectCurrentScopeFleetId(state);

	if (!fleetId) {
		// This shouldn't happen but nothing will load anyway, so we just return true
		return true;
	}

	const fleet = FleetSelectors.selectCurrentScopeFleet(state);

	if (!fleet.value) {
		// If we have a cached value, we can return that while the fleet data loads
		const cachedValue = StorageUtils.get(TRENDS_AVAILABLE_KEY, {})!;

		if (cachedValue[fleetId] !== undefined) {
			return cachedValue[fleetId];
		}

		// If the fleets have loaded we can check if it's a hardware fleet
		// If not we can let the user straight through
		const rootFleetObject = allFleets.value?.find(
			(fleet) => fleet.id === fleetId,
		);

		if (rootFleetObject && rootFleetObject?.appType !== HW_APP_TYPE) {
			return true;
		}

		// If we don't have a cached value and the fleet is a hardware fleet, we don't know yet so return undefined
		return undefined;
	}

	if (selectCurrentScopeAppType(state) !== HW_APP_TYPE) {
		return true;
	}

	const group = selectCurrentScopeGroup(state);
	const defaultValue = isFiveKeysTheme();

	return group
		? Boolean(group.settings?.geotabTrends ?? defaultValue)
		: Boolean(fleet.value.settings?.geotabTrends ?? defaultValue);
};

export const selectCurrentScopeAppType = (state: StoreState) => {
	const fleet = FleetSelectors.selectCurrentScopeFleet(state);

	if (!fleet.value) {
		return undefined;
	}

	return fleet.value?.appType ?? "software";
};

export const selectCurrentScopeVendor = (state: StoreState) => {
	const fleet = FleetSelectors.selectCurrentScopeFleet(state);

	if (!fleet.value) {
		return undefined;
	}

	return fleet.value?.vendor ?? "brightmile";
};

export const selectCurrentScopePillars = (state: StoreState) => {
	const fleet = FleetSelectors.selectCurrentScopeFleet(state);
	const defaultPillars = isFiveKeysTheme() ? FIVE_KEYS_PILLARS : PILLARS;

	if (!fleet.value) {
		return defaultPillars;
	}

	if (fleet.value.hardwarePillars) {
		return HardwarePillars.PILLARS.reduce((total: any, hwPillar) => {
			const pillarSettings: any = { ...hwPillar };
			const fleetPillar = fleet.value!.hardwarePillars!.find(
				(enabledPillar) => enabledPillar.type === hwPillar.id,
			);

			if (fleetPillar) {
				if (fleetPillar.target) {
					pillarSettings.target = fleetPillar.target;
				}

				if (fleetPillar.extras) {
					fleetPillar.extras.forEach((extra) => {
						const extraPillar: any = HardwarePillars.PILLARS.find(
							(hwPillarForExtra) => hwPillarForExtra.id === extra.type,
						);

						pillarSettings.subPillars = [
							...(pillarSettings.subPillars || []),
							{ ...extraPillar, target: extra?.target },
						];
					});
				}

				total.push(pillarSettings);
			}

			return total;
		}, []);
	} else {
		return defaultPillars;
	}
};

export const selectCurrentScopeRootGroupId = (state: StoreState) =>
	state.Groups.rootGroupId;

export const selectCurrentScopeRootGroup = (state: StoreState) => {
	const group = selectCurrentScopeGroup(state);
	return group?.rootGroup ? group : group?.rootGroupData;
};

export const selectCurrentScope = createSelector(
	[
		selectCurrentScopeFleetId,
		selectCurrentScopeGroupId,
		selectCurrentScopeRootGroupId,
		ProgramSelectors.getCurrentScopeProgramId,
	],
	(fleetId, group, rootGroupId, programId) =>
		({ fleetId, group, rootGroupId, programId } as ScopeInfo),
);

export const selectLoginRejectionCount = (state: StoreState) =>
	state.User.loginRejectionCount;

export const selectIsLoggedInUserImperial = (state: StoreState) =>
	selectLoggedInUser(state)?.imperial;

/** @deprecated */
export const selectCurrentScopeFleetName = createSelector(
	[selectCurrentScopeFleetId],
	(fleetId) => FormatUtils.getFleetDisplayName(fleetId),
);

/** @deprecated */
export const selectCurrentScopeName = createSelector(
	[
		selectCurrentScopeFleetName,
		selectLoggedInUserGroupId,
		selectLoggedInUserGroup,
	],
	(fleetName, groupId, selectedGroup) => {
		return groupId
			? selectedGroup
				? FormatUtils.getGroupDisplayName(selectedGroup)
				: StringUtils.toTitleCase(groupId)
			: fleetName;
	},
);

export const selectLoggedInUserFleetId = (state: StoreState) => {
	const loggedInUser = selectLoggedInUser(state);
	const emulatedUserLevel = AuthUtils.getEmulatedUserLevel();

	if (emulatedUserLevel) {
		const scopeFleetId = selectCurrentScopeFleetId(state);
		const emulatedFleetId = AuthUtils.getEmulatedFleetId();
		return emulatedFleetId || scopeFleetId;
	}

	return loggedInUser?.fleetId;
};

export const selectIsCurrentScopeFleetOutsider = createSelector(
	[
		selectCurrentScopeFleetId,
		selectLoggedInUserFleetId,
		FleetSelectors.getAllFleets,
	],
	(scopeFleetId, userFleetId, allFleets) => {
		return (
			scopeFleetId !== Flags.DEMO_FLEET_ID &&
			userFleetId !== scopeFleetId &&
			AuthUtils.getCurrentUserLevel() !== AuthLevel.SUPER_ADMIN &&
			!(
				AuthUtils.getCurrentUserLevel() === AuthLevel.ADMIN &&
				(allFleets.value || []).length > 1
			)
		);
	},
);

export const selectIsCurrentScopeGuest = (state: StoreState) => {
	const scopeFleetId = selectCurrentScopeFleetId(state);
	const loggedInUserFleetId = selectLoggedInUserFleetId(state);

	return (
		scopeFleetId !== Flags.DEMO_FLEET_ID &&
		loggedInUserFleetId !== scopeFleetId &&
		!isAuthLevelAccessAnyFleet()
	);
};
